import type { LoadingManager } from '@seddi/three';
import { load as loadFile } from '../loaders/binaryAssetLoader';
import type { StitchesTexturesDto } from './stitchesTexturesDtos';
import type { StitchesTextures } from './stitchesTexturesTypes';

/**
 * Load and parse json stitches textures file.
 * @param url - URI.
 */
const loadStitchesTextures = async (url: string): Promise<StitchesTexturesDto> => {
  try {
    const response = await fetch(url);

    const { status } = response;

    if (status < 200 || status >= 400) {
      throw new Error(status.toString());
    }

    return (await response.json()) as StitchesTexturesDto;
  } catch (err) {
    console.error(err);
  }
};

/**
 * Transform binary chunks of information into meaninful texture data.
 * @param dto - Descriptor.
 * @param buffer - Binary data.
 */
const parseStitchesTextures = async (
  dto: StitchesTexturesDto,
  buffer: Uint8Array,
): Promise<StitchesTextures> => {
  // transform buffers
  // quick solution until we can cross reference from the dto buffer uris
  const buffers = [buffer];
  // transform buffer views
  const bufferViews = dto.bufferViews.map((v) => {
    const buffer = new Uint8Array(buffers[v.buffer].buffer, v.byteOffset, v.byteLength);
    return { ...v, buffer };
  });
  // transform images
  const images = await Promise.all(
    dto.images.map(async (i) => {
      const buffer = bufferViews[i.bufferView].buffer;
      const blob = new Blob([buffer] as Uint8Array<ArrayBuffer>[]);
      const image = await createImageBitmap(blob, {
        colorSpaceConversion: 'none',
        imageOrientation: 'flipY',
      });
      return { ...i, image };
    }),
  );
  // transform pieces
  const pieces = dto.pieces.map((p) => {
    const materials = p.materials.map((m) => {
      const baseColorTexture = images[m.baseColorTexture];
      const normalTexture = images[m.normalTexture];
      return { ...m, baseColorTexture, normalTexture };
    });
    return { ...p, materials };
  });
  // output
  return { pieces };
};

/**
 * Load stitches textures.
 * @param url = Resource URL.
 * @param urlBinary = Resource binary URL.
 * @param manager - Loading manager.
 */
export const load = async (
  url: string,
  urlBinary: string,
  manager: LoadingManager,
): Promise<StitchesTextures> => {
  // load text/binary files from url
  const [dto, binary] = await Promise.all([
    loadStitchesTextures(url),
    loadFile(urlBinary, manager),
  ]);
  // load textures
  const textures = parseStitchesTextures(dto, binary);
  return textures;
};
